import _ from 'lodash'
import type { PageDefinition } from '@wix/thunderbolt-components-native'
import type {
	Language,
	BaseDataItem,
	BeckyFuncLibParams,
	Component,
	FetchFn,
	LayoutSize,
	MetricsReporter,
	ResolvedBuilderWidgetDataItem,
	ResolvedInnerElementsDataItem,
	SingleLayoutData,
	Spx,
	Structure,
	Style,
	StyleProperties,
	TransformationDataItem,
	TransformData,
	VariantRelation,
	VariantRelationViewItem,
	WidgetProps,
} from '@wix/thunderbolt-becky-types'
import { structure2mesh } from '@wix/thunderbolt-mesh'
import { convertSeoModel } from '@wix/advanced-seo-utils/converters'
import {
	color,
	convertEffectToAnimationDef,
	getDirection,
	getDisplayedId,
	getDisplayedItemId,
	getFullId,
	getFullItemId,
	getInflatedItemId,
	getInnerMostItemId,
	getItemOuterItemId,
	getMaxLayerSize,
	getOuterItemId,
	getRepeatedCompSelector,
	getRepeaterItemsAncestorChain,
	getRepeaterItemsFromProps,
	getTemplateCompIdAndRepeaterScope,
	getTemplateItemId,
	GHOST_COMP_TYPE,
	isDisplayedOnly,
	isRepeatedComponent,
	isRepeatedComponentOfTemplate,
	isRepeater,
	links,
	nameUUIDFromString,
	resolveSpxAttribute,
	sizeToString,
	text,
	Translations,
	vectorImage,
} from '@wix/thunderbolt-commons'
import type { BeckyFunctionLibrary, BeckyModel, CompInfo } from './types'
import { getDockedStyles } from './dockedLayer'
import * as functionLibraryExtensions from './functionLibraryExtensions'
import { determineFontFacesFontDisplay } from './functionLibraryExtensions'
import {
	getMeshOverrides,
	getMeshResult,
	getMeshResultForHoverBox,
	getMeshResultsForMasterPage,
} from './functionLibraryExtensions/mesh/meshFunctionLibrary'
import { createDataResolvingFunctionLibrary } from './functionLibraryExtensions/dataResolving/dataResolvingFunctionLibrary'
import { getColumnOverrides } from './functionLibraryExtensions/mesh/components/column'
import { refApi } from '@wix/thunderbolt-refs'
import { WidgetAPI } from '@wix/document-services-types'
import jsonSchemaUtils from '@wix/wix-json-schema-utils'
import { loadTranslations } from './utils/translationsLoader'
import type { CompProps } from '@wix/thunderbolt-symbols'

const globalCache = new WeakMap<object, Map<string, Promise<string>>>()

const stringifyAttrs = (attrs: Record<string, string>) => {
	return Object.keys(attrs)
		.map((k) => `${k}="${attrs[k]}"`)
		.join(' ')
}

const fetchUrlFactory = (fetch: FetchFn, reportError: MetricsReporter['reportError']) => {
	const fetchUrl: BeckyFunctionLibrary['fetchUrl'] = function (
		this: BeckyFunctionLibrary,
		url,
		fallbackValue,
		updateStore = () => {}
	) {
		if (!globalCache.has(this)) {
			globalCache.set(this, new Map())
		}

		const pendingRequests = globalCache.get(this)!
		const cachedRequest = pendingRequests.get(url)
		if (cachedRequest) {
			return cachedRequest
		}

		const svgRequest = fetch(url)
			.then((res) => (res.ok ? res.text() : null))
			.catch((e) => {
				reportError(e)
				return null
			})
			.then((txt) => {
				const res = txt === null ? fallbackValue : txt
				updateStore(res)
				return res
			})

		pendingRequests.set(url, svgRequest)

		updateStore(null)
		return svgRequest
	}

	return fetchUrl
}

const done = () => {}

const invoke = (cb: Function, ...args: Array<any>) => typeof cb === 'function' && cb(...args)

const invokeResolverFactory = (reportError: MetricsReporter['reportError']) => (
	cb: Function,
	extensionName: string,
	compId: string,
	compType: string,
	fallbackValue: any,
	compInfo: Partial<CompInfo>,
	shouldInjectRefApi: boolean,
	carmiData: any
) => {
	try {
		return cb ? cb({ ...compInfo, ...(shouldInjectRefApi && { refApi }) }, carmiData) : carmiData
	} catch (error) {
		const wrappedError = new Error(
			`Error resolving ${extensionName} for component ${compId} of type ${compType}: ${error?.message}`
		)

		console.error(wrappedError, error)
		reportError(wrappedError)

		return fallbackValue
	}
}

const cloneDeep = (value: any) => _.cloneDeep(value)

const castToNumber = (value: string) => Number(value)

const mapWidgetDefaultProps = (
	widgetDescriptor: WidgetAPI,
	customDefinitions: Array<Record<string, Object>>
): WidgetProps => {
	const propertiesSchemas = widgetDescriptor?.propertiesSchemas

	if (!propertiesSchemas && !Array.isArray(propertiesSchemas)) {
		return {}
	}

	const customDefinitionsMap =
		customDefinitions && Array.isArray(customDefinitions)
			? customDefinitions.reduce((acc, item) => {
					for (const key in item) {
						if (!acc.hasOwnProperty(key)) {
							acc[key] = item[key]
						}
					}
					return acc
			  }, {})
			: {}

	const resolver = jsonSchemaUtils.createResolver(jsonSchemaUtils.baseDefinitions, customDefinitionsMap)
	const resolvedProperies = propertiesSchemas.map((propSchema) => resolver.resolve(propSchema.structure))

	return resolvedProperies.reduce((defaultProps, property) => {
		const propertyName = Object.keys(property)[0]
		defaultProps[propertyName] = property[propertyName].default

		return defaultProps
	}, {})
}

const regExpMatch = (toFind: string, regExpStr: string) => toFind.match(new RegExp(regExpStr))

const getTranslation = (globalTranslations: Translations) => (namespace: string, key: string) => {
	return globalTranslations[namespace] && globalTranslations[namespace][key]
}

const getDirectionByLanguage = (language: string) => getDirection(language as Language)

const formatCssValue = (value: string | number, spx: Spx | undefined, keepNumValues: boolean = false) =>
	resolveSpxAttribute(value, spx, keepNumValues)

const getCssValueFormatter = (_config: {}) => (
	value: string | number,
	spx: Spx | undefined,
	keepNumValues: boolean = false
) => resolveSpxAttribute(value, spx, keepNumValues)

export const rgbToHex = (rgbString: string) => {
	if (!rgbString.includes(',')) {
		return rgbString
	}

	const componentToHex = (c: number) => {
		const hex = c.toString(16)
		return hex.length === 1 ? '0' + hex : hex
	}

	const [r, g, b] = rgbString.split(',').map(Number)
	return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

const getUndefined = () => void 0

export const seoMergeBlob = (
	originalPageData: PageDefinition['unresolvedData'],
	translatedPageData: PageDefinition['unresolvedData']
) => {
	const original = convertSeoModel(originalPageData?.advancedSeoData || '')
	const translated = convertSeoModel(translatedPageData?.advancedSeoData || '')

	const advancedSeoData = translatedPageData?.id ? translated : original
	return { ...originalPageData, ...translatedPageData, advancedSeoData: JSON.stringify(advancedSeoData) }
}

export const addCssValueToLayoutSizes = (
	singleLayoutData: SingleLayoutData | undefined
): SingleLayoutData | undefined => {
	if (!singleLayoutData) {
		return undefined
	}
	const addInLayout = (
		layoutType: 'componentLayout' | 'itemLayout',
		layoutSizePaths: Array<string | Array<string>>
	) => {
		layoutSizePaths.forEach((path) => {
			const layoutSize = _.get(singleLayoutData, _.flatten([layoutType, path])) as LayoutSize

			if (layoutSize) {
				_.set(singleLayoutData!, _.flatten([layoutType, path, 'cssValue']), sizeToString(layoutSize))
			}
		})
	}

	const layoutSizePathsInComponentLayout = ['width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight']
	const layoutSizePathsInItemLayout = [
		'left',
		'right',
		'top',
		'bottom',
		['margins', 'left'],
		['margins', 'right'],
		['margins', 'top'],
		['margins', 'bottom'],
	]

	addInLayout('componentLayout', layoutSizePathsInComponentLayout)
	addInLayout('itemLayout', layoutSizePathsInItemLayout)

	return singleLayoutData
}

const isMobileVariant = (item: BaseDataItem) => {
	return (
		item.type === 'VariantRelation' &&
		(item as VariantRelationViewItem<BaseDataItem>).variants.some((v) => v.type === 'Mobile')
	)
}

const getMobileVariant = (items: Array<BaseDataItem<string>>): VariantRelation =>
	items.find((l) => isMobileVariant(l)) as VariantRelation

export const getClassicLayout: BeckyFunctionLibrary['getClassicLayout'] = (
	layoutItems: Array<BaseDataItem>,
	isMobileView: boolean,
	isOneDocMigrated: boolean
) => {
	if (!isOneDocMigrated || !layoutItems) {
		return undefined
	}

	const compDefaultLayout = getCompDefaultLayout(layoutItems, isMobileView)

	return compDefaultLayout?.componentLayout?.type === 'ClassicComponentLayout' ? compDefaultLayout : undefined
}

export const getCompDefaultLayout: BeckyFunctionLibrary['getCompDefaultLayout'] = (
	layoutItems: Array<BaseDataItem>,
	isMobileView: boolean
) => {
	if (!layoutItems) {
		return undefined
	}

	if (isMobileView) {
		const mobileVariant = getMobileVariant(layoutItems)
		return mobileVariant ? (mobileVariant.to as SingleLayoutData) : undefined
	}

	return layoutItems[0]?.type === 'SingleLayoutData' ? (layoutItems[0] as SingleLayoutData) : undefined
}

export const getClassicTransformation: BeckyFunctionLibrary['getClassicTransformation'] = (
	transformationItems: Array<TransformationDataItem>,
	isMobileView: boolean,
	isOneDocMigrated: boolean
) => {
	if (!isOneDocMigrated || !transformationItems?.length) {
		return undefined
	}

	if (isMobileView) {
		const mobileVariant = getMobileVariant(transformationItems)
		return mobileVariant ? (mobileVariant.to as TransformData) : undefined
	}

	return transformationItems[0].type === 'TransformData' ? (transformationItems[0] as TransformData) : undefined
}

const getColorIndex = (colorName: string) => Number(colorName.split('_').pop())

const collectNestedStructure = (root: BeckyModel['structure']['string'], structure: BeckyModel['structure']) => {
	// simple BFS impl to collect entire nested tree as flat object
	const queue: Array<string> = root.components ? [...root.components] : []
	const nestedStructure: BeckyModel['structure'] = {}

	while (queue.length) {
		const childId = queue.pop() as string
		const child = structure[childId]
		nestedStructure[childId] = child
		queue.push(...(child.components || []))
	}
	return nestedStructure
}

function removeGhosts(structure: Structure, ghostComponents: Structure, appWidgetGhosts: Structure): Structure {
	const hiddenCompGhosts = 'hiddenCompGhosts'
	const ghostedByCompProp = 'ghostedByCompProp'
	const GHOST_COMP_STRUCTURE_OVERRIDES = {
		components: [],
		componentType: GHOST_COMP_TYPE,
		connectionQuery: '',
		uiType: '',
		slotsQuery: '', // remove slotsQuery pointer to avoid resolving slots for ghost comps in flattenStructure
	}
	const ghostCompsByType = Object.values(ghostComponents).reduce<
		Record<typeof hiddenCompGhosts | typeof ghostedByCompProp, Array<Component>>
	>(
		(acc, comp) => {
			if (comp.isHiddenInAllVariants) {
				acc[hiddenCompGhosts].push(comp)
			} else {
				acc[ghostedByCompProp].push(comp)
			}
			return acc
		},
		{ hiddenCompGhosts: [], ghostedByCompProp: [] }
	)

	const ghostCompsNestedStructure = ghostCompsByType[ghostedByCompProp].map((comp) =>
		collectNestedStructure(comp, structure)
	)
	const componentsToRemove = [...Object.keys(ghostCompsNestedStructure), ...Object.keys(appWidgetGhosts)]

	const hiddenGhostsNestedStructure = ghostCompsByType[hiddenCompGhosts].map((comp) =>
		collectNestedStructure(comp, structure)
	)
	const componentsToGhostify = Object.keys(hiddenGhostsNestedStructure)

	return Object.keys(structure).reduce<Record<string, Component>>((acc, compId) => {
		if (componentsToRemove.includes(compId)) {
			return acc
		}

		const comp = structure[compId]

		if (comp.components) {
			comp.components = comp.components.filter(
				(childId) => !componentsToRemove.includes(childId) && !componentsToGhostify.includes(childId)
			)
		}

		const isGhost = ghostComponents[compId] || componentsToGhostify.includes(compId)
		acc[compId] = isGhost ? { ...comp, ...GHOST_COMP_STRUCTURE_OVERRIDES } : comp

		return acc
	}, {})
}

export const removeHash = (value?: string) => (typeof value === 'string' ? value.replace('#', '') : value)

export const getPathsContainerOriginalId = (compId: string) => compId.slice(0, -'-paths-container'.length)

const getDraftIds: BeckyFunctionLibrary['getDraftIds'] = (item) => {
	if (item?._draftRefs) {
		const props = item._draftRefs.map(({ path = [''] }) => path[0]) // currently we only really support path of length 1
		return (_(item)
			.pick(props)
			.mapValues((value) => (_.isArray(value) ? _.map(value, removeHash) : removeHash(value)))
			.value() as unknown) as Record<string, string | Array<string>>
	}
	return null
}

const getResolvedRefs: BeckyFunctionLibrary['getResolvedRefs'] = (refs, refMap) => refs.map((refId) => refMap[refId])

const getStyleBasedClassNames = (style: StyleProperties, compProps: CompProps) => {
	const { themeOverrides } = style
	const classes = `${compProps?.className || ''} ${themeOverrides ? 'spxThemeOverride' : ''}`.trim()
	return classes === '' ? undefined : classes
}

const getStylePropertiesWithSpxAndOverrides = (style: Style) => {
	const {
		spx,
		themeOverrides,
		style: { properties },
	} = style
	return { ...properties, spx, themeOverrides }
}

const fixComponentsArrayForPathsContainer = (compId: string, suffix: string, components: Array<string>) =>
	components.map((comp) => (comp === compId ? `${comp}${suffix}` : comp))

export const getScaledFontSize = (minTextScaleFontSize: string, maxTextScaleFontSize: string) => {
	const minFontWithoutUnits = parseFloat(minTextScaleFontSize)
	const maxFontWithoutUnits = parseFloat(maxTextScaleFontSize)
	const fontSizeDiff = `${maxFontWithoutUnits - minFontWithoutUnits}`
	const viewportDiff = '(var(--maxViewportSize) - var(--minViewportSize))'
	return `calc(${minFontWithoutUnits}px + ${fontSizeDiff} * (var(--customScaleViewportLimit) - var(--minViewportSize) * 1px) / ${viewportDiff})`
}

const getInnerElementsOverridesMaps = (
	compInnerElementsDataItem: ResolvedBuilderWidgetDataItem
): {
	innerElementsOverrides: Record<string, any>
	dataQueries: Array<string>
} => {
	const innerElementsOverrides: any = {}
	const dataQueries: Array<string> = []
	let currInnerElement = innerElementsOverrides

	const recurInnerElements = (innerElementsDataItem: ResolvedBuilderWidgetDataItem, compPath: string) => {
		const overridesMap = _.omit(innerElementsDataItem, ['id', 'metaData', 'type'])
		_.keys(overridesMap).forEach((nickname) => {
			const currNicknamePath = compPath.length ? `${compPath}/${nickname}` : nickname
			currInnerElement[nickname] = {}
			const currInnerElementData: ResolvedInnerElementsDataItem = _.get(overridesMap, nickname)

			if (currInnerElementData.dataQuery) {
				const dataQuery = removeHash(currInnerElementData.dataQuery)!
				currInnerElement[nickname] = dataQuery
				dataQueries.push(dataQuery)
			}

			if (currInnerElementData.innerElementsQuery) {
				currInnerElement = currInnerElement[nickname]
				recurInnerElements(currInnerElementData.innerElementsQuery, currNicknamePath)
			}
		})
	}

	recurInnerElements(compInnerElementsDataItem, '')

	return { innerElementsOverrides, dataQueries }
}

/**
 * Resolves inner elements properties by replacing string references with actual data items.
 * This function modifies the data to be the actual props expected to pass to the component, using the 'elementProps' notation and excluding internal properties.
 *
 * @param {Record<string, any>} innerElementsDataOverrides - The overrides for inner elements.
 * @param {Record<string, any>} dataItems - The data items to use for resolving references.
 * @returns {Record<string, any>} - The resolved inner elements props.
 */
const resolveInnerElementsProps = (innerElementsDataOverrides: Record<string, any>, dataItems: Record<string, any>) => {
	const replaceLeaves = (obj: Record<string, any>) => {
		for (const key in obj) {
			const val = obj[key]
			if (typeof val === 'string') {
				obj[key] = _.omit(dataItems[val], 'id', 'type')
			} else {
				const wrappedObj = { elementProps: {} }
				wrappedObj.elementProps = _.omit(val, 'id', 'type')

				replaceLeaves(wrappedObj.elementProps)
				obj[key] = wrappedObj
			}
		}
	}

	replaceLeaves(innerElementsDataOverrides)

	return innerElementsDataOverrides
}

const createFunctionLibrary = ({ metricsReporter, fetch, schemas }: BeckyFuncLibParams): BeckyFunctionLibrary => {
	const dataResolvingFunctionLibrary = createDataResolvingFunctionLibrary(schemas)
	const { runAndReport, runAsyncAndReport, reportError } = metricsReporter

	const { buildSvgUrl, getSvgBaseUrl } = vectorImage.buildSvgUrlFactory()

	return {
		loadTranslations(language: string): Promise<Translations> {
			return loadTranslations(language as Language)
		},
		isPageUriSEOTranslated: (
			pageIdToPageUriSEO: Record<string, string>,
			pageIdToPageUriSEOFromTranslation: Record<string, Record<string, string>>
		) =>
			!_.every(pageIdToPageUriSEOFromTranslation, (pagesData) =>
				_.every(pagesData, (pageUriSEO, pageId) => {
					return pageIdToPageUriSEO[pageId] === pageUriSEO
				})
			),
		isArray: _.isArray,
		sortBy: _.sortBy,
		trim: (str: string) => str.trim(),
		removeHash,
		fixStructureMetaData: (structure: Structure, originalCompMetaData: Component['metaData']): Structure =>
			_.mapValues(structure, (comp) => ({
				...comp,
				metaData: { ...originalCompMetaData, ...comp?.metaData },
			})),
		replace: (value: string, searchValue: string | RegExp, replaceValue: string) =>
			value.replace(searchValue, replaceValue),
		getRegExp: (pattern: string, flags?: string) => new RegExp(pattern, flags),
		split: (str: string, delimiter: string) => str.split(delimiter),
		structure2mesh,
		getWixcodeConnection: (role: string, compId: string, controllerId: string) => ({
			controllerId,
			[role]: compId,
		}),
		kebabCase: _.kebabCase,
		includes: (str: string, searchString: string) => str.includes(searchString),
		merge: (objects: Array<any>) => _.merge({}, ...objects),
		mergeAndConcat: (objects: Array<any>) =>
			_.mergeWith({}, ...objects, (objValue: any, srcValue: any) => {
				if (_.isArray(objValue)) {
					return objValue.concat(srcValue)
				}
			}),
		parseInt: (val: string) => parseInt(val, 10),
		parseJSON: (val: string) => {
			if (!val) {
				return val
			}

			try {
				return JSON.parse(val)
			} catch (e) {
				if (process.env.NODE_ENV === 'development') {
					console.warn('parseJSON failed parsing value:', val)
				}
				return null
			}
		},
		serializeJSON: (val: object) => JSON.stringify(val),
		doubleQuotesEscapeForIE: (str: string) => str.replace(/''/g, "'"),
		encodeURIComponent,
		mathFloor: Math.floor,
		parseFloat,
		getDockedStyles,
		getMetricsReporter: () => metricsReporter,
		fetch,
		fetchUrl: fetchUrlFactory(fetch, reportError),
		buildSvgUrl,
		getSvgBaseUrl,
		stringifyAttrs,
		invoke,
		getDeletedItems: (uniqueIdentifier, getCurrentValue, getPreviousVersionStore) => {
			const store = getPreviousVersionStore()
			const previous = store.get(uniqueIdentifier) || {}
			const currentValue = getCurrentValue()
			const deletedItems = _.omitBy(previous, (val, key) => _.has(currentValue, key))
			store.set(uniqueIdentifier, { ...currentValue })

			return deletedItems
		},
		invokeResolver: invokeResolverFactory(reportError),
		regExpMatch,
		castToNumber,
		getUndefined,
		getTranslation,
		getDirectionByLanguage,
		rgbToHex,
		getColorIndex,
		...dataResolvingFunctionLibrary,
		...vectorImage,
		...color,
		...links,
		...text,
		getFullId,
		getTemplateItemId,
		getTemplateCompIdAndRepeaterScope,
		getFullItemId,
		getOuterItemId,
		getDisplayedId,
		isDisplayedOnly,
		getInflatedItemId,
		isRepeatedComponent,
		isRepeatedComponentOfTemplate,
		getRepeaterItemsAncestorChain,
		getItemOuterItemId,
		getDisplayedItemId,
		getInnerMostItemId,
		isRepeater,
		runAsyncAndReport,
		runAndReport,
		reportError: (msg: string) => {
			reportError(new Error(msg))
		},
		...functionLibraryExtensions,
		seoMergeBlob,
		getMeshResult,
		getMeshResultsForMasterPage,
		getMeshResultForHoverBox,
		getMeshOverrides,
		nameUUIDFromString,
		done,
		getDraftIds,
		getResolvedRefs,
		cloneDeep,
		collectNestedStructure,
		getColumnOverrides,
		loadPages: () => {},
		mapWidgetDefaultProps,
		convertEffectToAnimationDef,
		getRepeaterItemsFromProps,
		getRepeatedCompSelector,
		getMaxLayerSize,
		getCssValueFormatter,
		formatCssValue,
		processOoiCss: () => '',
		getStyleBasedClassNames,
		getStylePropertiesWithSpxAndOverrides,
		addCssValueToLayoutSizes,
		getClassicLayout,
		getCompDefaultLayout,
		getClassicTransformation,
		removeGhosts,
		getPathsContainerOriginalId,
		fixComponentsArrayForPathsContainer,
		determineFontFacesFontDisplay,
		getScaledFontSize,
		getInnerElementsOverridesMaps,
		resolveInnerElementsProps,
		encodeURI,
	}
}

export { createFunctionLibrary }
